import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { FixedSizeList } from 'react-window';
import { DropoffLocationDistance } from '../../types/types';

interface DropoffListProps {
  list: DropoffLocationDistance[];
  dropoffName: string;
  onClick: (dropoff: DropoffLocationDistance, dropoffName: string) => void;
  disabled: boolean;
}

const LIST_WIDTH = 600;
const LIST_HEIGHT = 400;
const ROW_HEIGHT = 52;

export default function DropoffList(props: DropoffListProps) {
  const [searchText, setSearchText] = useState('');

  const matchingDropoffs = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) {
      return props.list;
    }

    return props.list.filter((dropoff) => (dropoff.record.get('Name') || '').toLowerCase().includes(search));
  }, [props.list, searchText]);

  const rowRender = ({ index, style }) => {
    const name = matchingDropoffs[index].record.get('Name');

    return (
      <MenuItem
        disabled={props.disabled}
        onClick={() => props.onClick(matchingDropoffs[index], props.dropoffName)}
        style={style}
        key={name}
      >
        <Typography variant="caption" key={name}>
          {name}{' '}
          {matchingDropoffs[index].distance !== undefined
            ? ` - ${Math.round(matchingDropoffs[index].distance)} mi`
            : ''}{' '}
        </Typography>
      </MenuItem>
    );
  };

  return (
    // tabIndex -1 keeps the MUI menu list from treating this wrapper as its active item
    <Box tabIndex={-1} width={LIST_WIDTH}>
      <Box sx={{ paddingX: 1, paddingTop: 1 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Search dropoffs"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onKeyDown={(event) => {
            // The menu list treats single characters as type-ahead and Home/End as
            // focus jumps - those keys belong to the search field while it has focus.
            if (event.key.length === 1 || event.key === 'Home' || event.key === 'End') {
              event.stopPropagation();
            }
          }}
        />
      </Box>

      {matchingDropoffs.length ? (
        // key resets the scroll position whenever the search changes the list
        /* 
        @ts-ignore it is not clear what exactly is wrong with these types. Could be the children */
        <FixedSizeList
          key={searchText}
          height={Math.min(LIST_HEIGHT, matchingDropoffs.length * ROW_HEIGHT)}
          width={LIST_WIDTH}
          itemSize={ROW_HEIGHT}
          itemCount={matchingDropoffs.length}
        >
          {rowRender}
        </FixedSizeList>
      ) : (
        <Typography variant="body2" sx={{ padding: 2 }}>{`No dropoffs match "${searchText}"`}</Typography>
      )}
    </Box>
  );
}
